import { MainLayout } from '../components/layouts/MainLayout';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import NewMovieGridVertical from "../components/movie-grid/NewMovieGridVertical";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { IGenre } from "../interfaces/IGenre";
import SearchInput from "../components/inputs/SearchInput";
import { AppMainStateContext } from "../contexts/AppMainStateContext.tsx";
import { ChannelsContext } from "../contexts/ChannelsContext.tsx";
import { IChannel } from '../interfaces/IChannel.ts';
import { useChannelsService } from '../hooks/useChannelsService.tsx';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import UtilsHelper from "../helpers/utilsHelper.ts";

interface IGenreWithStatus extends IGenre {
    status: boolean
}

export default function AllChannels() {
    // contexts
    const { setFilteredChannels } = useContext(ChannelsContext);
    const { isMobile, isModalOpen } = useContext(AppMainStateContext);
    // services
    const { channels, genres, getChannels, updateEpgListToActualTime } = useChannelsService();
    // states
    const [selectedGenres, setSelectedGenres] = useState<IGenreWithStatus[]>([]);
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

    const [searchInput, setSearchInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    // refs
    const filteredChannelsPopupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!selectedGenres.length)
            setSelectedGenresInitialValue();
    }, [genres]);

    useEffect(() => {
        UtilsHelper.changeWindowDocumentTitle("Kanały TV");
        setIsLoading(true);
        getChannels();
        setIsLoading(false);
    }, [getChannels]);

    const filterChannelsByGenres = (selectedGenres: IGenreWithStatus[]) => {
        setFilteredChannels([...channels]);
        const selectedGenresList = selectedGenres.filter((x) => x.status);

        let channelsTemp: IChannel[] = [];
        if (!selectedGenresList.length) setFilteredChannels([...channels]);
        selectedGenresList.forEach((x) => {
            const filterChannelList = channels.filter((y) => y.genre_id === x.id);

            if (filterChannelList) {
                channelsTemp = [...channelsTemp, ...filterChannelList];
                setFilteredChannels([...channelsTemp])
            }
        })
    }

    const toggleGenreStatusButton = (genre: IGenreWithStatus) => {
        const selectedGenresTemp = selectedGenres;
        const genreIndex = selectedGenresTemp.findIndex(x => x.id === genre.id);
        selectedGenresTemp[genreIndex].status = !selectedGenresTemp[genreIndex].status;
        setSelectedGenres([...selectedGenresTemp]);
        filterChannelsByGenres(selectedGenres);
        scrollToTop();
    }

    const setSelectedGenresInitialValue = () => {
        const selectedCategoryTemp: IGenreWithStatus[] = [];
        genres.map((genre) => {
            selectedCategoryTemp.push({ status: false, ...genre })
        })
        setSelectedGenres([...selectedCategoryTemp]);
        setFilteredChannels(channels);
        scrollToTop();
    }

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value);
    }

    const scrollToTop = () => {
        const body = document.querySelector('main');
        const html = document.querySelector('html');
        if (body)
            body.scrollTo({ top: 0, behavior: "smooth" });
        if (html)
            html.scrollTo({ top: 0, behavior: "smooth" });
    }

    useEffect(() => {
        let updateEpgListInterval: number;
        if (!isModalOpen) {
            updateEpgListInterval = setInterval(() => {
                updateEpgListToActualTime(channels);
            }, 10000)
        }
        return () => clearInterval(updateEpgListInterval);
    }, [isModalOpen, channels]);

    return (
        <MainLayout isFooterVisible={true} contentPadding='p-2 pt-16 md:pt-0'>
            {/* <div className={`${isMobile ? "px-2" : "px-5"} flex-1 flex flex-col mb-4 justify-between`}> */}
            {/* <div className={`min-h-full`}> */}
                <div
                    ref={filteredChannelsPopupRef}
                    className={`popup pt-2 max-h-[50vh] pb-4 fixed flex flex-col items-center top-28 md:top-14 right-0 bg-white rounded space-y-4 shadow-3xl z-50 ${isMobile ? "top-12" : "top-12"}`}
                    data-open={isFiltersModalOpen ? isFiltersModalOpen : undefined}
                    onAnimationEnd={() => filteredChannelsPopupRef.current?.removeAttribute('close')}
                >
                    {isFiltersModalOpen &&
                        <div className={"px-8 flex-col space-y-6 flex-1 overflow-y-scroll"}>
                            <p className={'self-start font-bold pt-6 text-neutral-600 text-lg'}>Kategorie</p>
                            <ul className={`${filteredChannelsPopupRef ? '' : 'hidden'} space-y-3 pb-1 font-light text-neutral-500`}>
                                {selectedGenres.map((genre) =>
                                    <li key={genre.id}
                                        className={'space-x-24 justify-between min-w-64 flex items-center'}>
                                        <p>{genre.name}</p>
                                        <button
                                            type="button"
                                            className="justify-self-end group relative inline-flex h-5 w-10 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-[#008DD2] focus:ring-offset-2 ring-offset-white"
                                            role="switch"
                                            aria-checked="false"
                                            onClick={() => toggleGenreStatusButton(genre)}
                                        >
                                            <span className="sr-only">Use setting</span>
                                            <span aria-hidden="true"
                                                className="pointer-events-none absolute h-full w-full rounded-md" />
                                            <span aria-hidden="true"
                                                className={`${genre.status ? 'bg-[#008DD2]' : 'bg-gray-300'} pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out`} />
                                            <span aria-hidden="true"
                                                className={`${genre.status ? 'translate-x-5' : 'translate-x-0'} pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out`} />
                                        </button>
                                    </li>
                                )}
                            </ul>
                        </div>
                    }
                    {isFiltersModalOpen &&
                        <>
                            <div className={"w-full h-[1px] bg-neutral-200"} />
                            <div className={"text-neutral-400 hover:text-neutral-600 transition"}>
                                <button className={"flex items-center space-x-1"}
                                    onClick={() => setSelectedGenresInitialValue()}>
                                    <XMarkIcon className={"w-5 h-5"} />
                                    <span>Wyczyść</span>
                                </button>
                            </div>
                        </>
                    }
                </div>
            {/* </div> */}

            <section className={"flex flex-col space-y-16 w-full"}>
                <div className={"flex items-center justify-between fixed top-14 md:top-0 left-0 md:left-24 right-0 z-30 bg-neutral-800 py-2 space-x-2 mx-0 px-2"}>
                    <SearchInput
                        value={searchInput}
                        placeholder={"Szukaj kanału..."}
                        className={"flex-1 md:w-60 md:flex-none"}
                        onInputChange={handleInputChange}
                    />
                    {isMobile ?
                        <button
                            className={`shadow shadow-neutral-600 rounded-full p-2 transition-colors ${isFiltersModalOpen ? "bg-[#007CC1]" : "bg-neutral-600"}`}
                            onClick={() => {
                                if (isFiltersModalOpen && filteredChannelsPopupRef.current)
                                    filteredChannelsPopupRef.current.setAttribute("close", '');
                                setIsFiltersModalOpen(!isFiltersModalOpen)
                            }}
                        >
                            <AdjustmentsHorizontalIcon className='w-6 h-6' />
                        </button>
                        :
                        <button
                            className={`shadow shadow-neutral-600 rounded-full border border-neutral-500 p-2 transition-colors ${isFiltersModalOpen ? "bg-[#007CC1]" : "bg-neutral-600 hover:bg-neutral-500"}`}
                            onClick={() => {
                                if (isFiltersModalOpen && filteredChannelsPopupRef.current)
                                    filteredChannelsPopupRef.current.setAttribute("close", '');
                                setIsFiltersModalOpen(!isFiltersModalOpen)
                            }}
                        >
                            <AdjustmentsHorizontalIcon className='w-6 h-6' />
                        </button>
                    }
                </div>
                <NewMovieGridVertical isLoading={isLoading}
                    channels={channels.filter(x => x.name.toLowerCase().includes(searchInput.toLowerCase()) && (!selectedGenres.find(y => y.status) && x.enabled && true || selectedGenres.find(y => y.id === x.genre_id)?.status && x.enabled))}
                />
            </section>
        </MainLayout>
    )
}
